import * as t from 'io-ts'
import api from '../common/http/api'
import {
  Interactions,
  InteractionsCodec,
  Playlist,
  PlaylistCodec
} from '../common/types'
import {validate} from '../common/types/validate'

export const API_URL = process.env.REACT_APP_API_URL || ''

const AuthCodeCodec = t.intersection([
  t.type({
    access_token: t.string,
    expires_in: t.number
    // accessTokenExpirationTimestampMs:
  }),
  t.partial({
    refresh_token: t.string
  })
])

const validateCodec = <C>(data: unknown, codec: t.Type<C>): C => {
  const type = validate(data, codec)
  if (!type.valid) {
    throw new Error(type.errors.join(','))
  }
  return type.data
}

export type API = ReturnType<typeof init>

export const init = (
  getToken: () => string | undefined,
  refreshToken: () => Promise<void>
) => {
  const playlists = api(API_URL, getToken, refreshToken)
  const eventSource: Record<string, Promise<EventSource>> = {}

  const getEventSource = async (attempt = 1) => {
    const token = getToken() ?? ''
    const wait = 800
    const key = token ? 'user' : 'public'
    if (key in eventSource) {
      return eventSource[key]
    }

    if (attempt > 2) {
      throw new Error('Unable to open EventSource')
    }

    eventSource[key] = new Promise<EventSource>((resolve, reject) => {
      try {
        const t = setTimeout(
          () => reject(`Open event source timed out ${wait}ms`),
          wait
        )
        const source = new EventSource(
          `${API_URL}/sse${token ? `?token=${token}` : ''}`
        )
        source.onopen = () => {
          clearTimeout(t)
          console.log('EventSource opened')
          resolve(source)
        }
        source.addEventListener('error', ev => {
          clearTimeout(t)
          reject(`EventSource addEventListener error, attempt:${attempt}`)
        })
      } catch (e) {
        console.debug('Caught exception creating EventSource', e)
        reject(e)
      }
    }).catch(e => {
      console.log('Error, Retrying EventSource', e)
      delete eventSource[key]
      return refreshToken().then(() => getEventSource(attempt + 1))
    })

    return eventSource[key]
  }

  const getPlaylists = async () => {
    const res = await playlists('get', '/playlists')
    if (!res.ok) {
      throw new Error(res.body)
    }
    return validateCodec(res.json, t.array(PlaylistCodec))
  }

  const subPlaylists = (onMessage: (playlists: Playlist[]) => void) => {
    const listener: EventListener = event => {
      if ('data' in event) {
        onMessage(
          validateCodec(
            //@ts-ignore
            JSON.parse(event.data),
            t.array(PlaylistCodec)
          )
        )
      }
    }
    const eventSourcePromise = getEventSource()
    eventSourcePromise
      .then(es => {
        es.addEventListener('playlists', listener)
      })
      .catch(e => {
        console.debug('failed to subscribe to playlists', e)
      })
    getPlaylists().then(onMessage)
    return () => {
      eventSourcePromise.then(es =>
        es.removeEventListener('playlists', listener)
      )
    }
  }

  const getCollection = async (userId: string) => {
    const res = await playlists('get', `/collection/${userId}`)
    if (!res.ok) {
      throw new Error(res.body)
    }
    return validateCodec(res.json, t.array(PlaylistCodec))
  }

  const subCollection = (
    userId: string,
    onMessage: (playlists: Playlist[]) => void
  ) => {
    const listener: EventListener = event => {
      if ('data' in event) {
        onMessage(
          validateCodec(
            //@ts-ignore data is in event
            JSON.parse(event.data),
            t.array(PlaylistCodec)
          )
        )
      }
    }
    const eventSourcePromise = getEventSource()
    eventSourcePromise
      .then(es => es.addEventListener('collection', listener))
      .catch(e => {
        console.debug('failed to subscribe to collection', e)
      })
    getCollection(userId).then(onMessage)
    return () => {
      eventSourcePromise.then(es =>
        es.removeEventListener('collection', listener)
      )
    }
  }

  const getInteractions = async () => {
    const res = await playlists('get', '/user')
    if (!res.ok) {
      throw new Error(res.body)
    }
    return validateCodec(res.json, InteractionsCodec)
  }

  const subInteractions = (onMessage: (interactions: Interactions) => void) => {
    const listener: EventListener = event => {
      if ('data' in event) {
        onMessage(
          validateCodec(
            //@ts-ignore data is in event
            JSON.parse(event.data),
            InteractionsCodec
          )
        )
      }
    }
    const eventSourcePromise = getEventSource()
    eventSourcePromise
      .then(es => es.addEventListener('interactions', listener))
      .catch(e => {
        console.debug('failed to subscribe to interactions', e)
      })
    getInteractions().then(onMessage)
    return () => {
      eventSourcePromise.then(es =>
        es.removeEventListener('interactions', listener)
      )
    }
  }

  const spotifyAuth = async (code: string, redirectUri: string) => {
    const res = await api(API_URL)('get', '/authorize', {code, redirectUri})
    const type = validate(res.json, AuthCodeCodec)
    if (!type.valid) {
      console.error('Invalid response for spotifyAuth', res.json)
      throw new Error(type.errors.join(','))
    }
    return type.data
  }

  const add = async (
    playlistId: string,
    data?: {frontPage?: boolean; parents?: string[]}
  ) => {
    // ensure we have a connection to event source open, so we see the result
    await getEventSource()
    const {ok} = await playlists('post', `/playlist-user/${playlistId}`, data)
    return ok
  }

  const remove = async (playlistId: string) => {
    // ensure we have a connection to event source open, so we see the result
    await getEventSource()
    const {ok} = await playlists('delete', `/playlist-user/${playlistId}`)
    return ok
  }

  const removeFrontPage = async (playlistId: string) => {
    await getEventSource()
    const {ok} = await playlists('delete', `/playlist/${playlistId}`)
    return ok
  }

  const voteUp = async (playlistId: string) => {
    // ensure we have a connection to event source open, so we see the result
    await getEventSource()
    const {ok} = await playlists('post', `/vote/${playlistId}`)
    return ok
  }

  const voteDown = async (playlistId: string) => {
    // ensure we have a connection to event source open, so we see the result
    await getEventSource()
    const {ok} = await playlists('delete', `/vote/${playlistId}`)
    return ok
  }

  return {
    add,
    getPlaylists,
    subPlaylists,
    getCollection,
    subCollection,
    subInteractions,
    getInteractions,
    remove,
    removeFrontPage,
    spotifyAuth,
    voteDown,
    voteUp
  }
}

export const getToken = () => {
  return localStorage.getItem('token') ?? undefined
}

export const storeToken = (token?: string) => {
  token
    ? localStorage.setItem('token', token)
    : localStorage.removeItem('token')
}

export const storeRefreshToken = (token?: string) => {
  token
    ? localStorage.setItem('refresh_token', token)
    : localStorage.removeItem('refresh_token')
}

export const refreshToken = async () => {
  console.log('refreshing token')
  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) {
    throw new Error('no refresh token available')
  }
  const res = await api(API_URL)('get', '/refresh', {
    refresh_token: refreshToken
  })
  if (!res.ok) {
    storeRefreshToken(undefined)
    storeToken(undefined)
    throw new Error('refresh token failed')
  }
  const decode = validate(res.json, AuthCodeCodec)
  if (decode.valid) {
    storeToken(decode.data.access_token)
    if (decode.data.refresh_token) {
      storeRefreshToken(decode.data.refresh_token)
    }
  }
}

export const Api = init(getToken, refreshToken)
